import React, { useState } from 'react';
const CAD_RATE = 1.38;
export function RestaurantProfitCalculator() {
  const [country, setCountry] = useState<'USA' | 'Canada'>('USA');
  const [dailyCustomers, setDailyCustomers] = useState(100);
  const [orderingPercentage, setOrderingPercentage] = useState(70);
  const [bottleCasesPerMonth, setBottleCasesPerMonth] = useState(20);
  const [caseCostUSD, setCaseCostUSD] = useState(30);
  const [menuPriceUSD, setMenuPriceUSD] = useState(2.0);
  const isCAD = country === 'Canada';
  const currencySymbol = isCAD ? 'CA$' : 'US$';
  const leaseCost = isCAD ? 399 : 299;
  // Convert to local currency
  const caseCost = isCAD ? Math.round(caseCostUSD * CAD_RATE) : caseCostUSD;
  const menuPrice = isCAD ? +(menuPriceUSD * CAD_RATE).toFixed(2) : menuPriceUSD;
  // Calculations
  const customersPerMonth = dailyCustomers * 30;
  const waterCustomersPerMonth = customersPerMonth * orderingPercentage / 100;
  // Traditional bottled water costs (savings)
  const traditionalMonthlyCost = bottleCasesPerMonth * caseCost;
  // WELLHAUS revenue
  const wellhausMonthlyRevenue = waterCustomersPerMonth * menuPrice;
  // Net profit calculation
  const wellhausNetProfit = wellhausMonthlyRevenue - leaseCost + traditionalMonthlyCost;
  // Annual profit
  const annualProfit = wellhausNetProfit * 12;
  // Format number with commas
  const formatNumber = (num: number) => {
    return Math.round(num).toLocaleString('en-US');
  };
  // Slider ranges adjusted for currency
  const caseCostMin = isCAD ? Math.round(20 * CAD_RATE) : 20;
  const caseCostMax = isCAD ? Math.round(45 * CAD_RATE) : 45;
  const menuPriceMin = isCAD ? +(1 * CAD_RATE).toFixed(2) : 1;
  const menuPriceMax = isCAD ? +(5 * CAD_RATE).toFixed(2) : 5;
  const menuPriceStep = isCAD ? 0.35 : 0.25;
  return <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-79">
      <h3 className="text-3xl md:text-4xl font-light mb-4 text-center" data-id="element-80">
        Restaurant Beverage Profit Calculator
      </h3>
      <p className="text-lg text-gray-600 text-center mb-8 max-w-3xl mx-auto" data-id="element-81">
        See how the WELLHAUS™ Drinkstation can transform your restaurant's
        beverage profit margins. Adjust the parameters below based on your
        business.
      </p>

      {/* Country Toggle */}
      <div className="flex justify-center mb-12" data-id="element-82">
        <div className="inline-flex bg-gray-100 rounded-full p-1" data-id="element-83">
          <button onClick={() => setCountry('USA')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${country === 'USA' ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-84">
            🇺🇸 USA (USD)
          </button>
          <button onClick={() => setCountry('Canada')} className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${country === 'Canada' ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-85">
            🇨🇦 Canada (CAD)
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-86">
        {/* Input Controls */}
        <div className="space-y-8" data-id="element-87">
          {/* Daily Customers */}
          <div data-id="element-88">
            <label className="flex items-center justify-between mb-3" data-id="element-89">
              <span className="text-base font-medium text-gray-900" data-id="element-90">
                Average Daily Customers
              </span>
              <span className="text-2xl font-light text-gray-900" data-id="element-91">
                {dailyCustomers}
              </span>
            </label>
            <input type="range" min="20" max="500" step="10" value={dailyCustomers} onChange={e => setDailyCustomers(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900" data-id="element-92" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-93">
              <span data-id="element-94">20</span>
              <span data-id="element-95">500</span>
            </div>
          </div>

          {/* Ordering Percentage */}
          <div data-id="element-96">
            <label className="flex items-center justify-between mb-3" data-id="element-97">
              <span className="text-base font-medium text-gray-900" data-id="element-98">
                % Ordering Water (typically 70%)
              </span>
              <span className="text-2xl font-light text-gray-900" data-id="element-99">
                {orderingPercentage}%
              </span>
            </label>
            <input type="range" min="30" max="100" step="5" value={orderingPercentage} onChange={e => setOrderingPercentage(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900" data-id="element-100" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-101">
              <span data-id="element-102">30%</span>
              <span data-id="element-103">100%</span>
            </div>
          </div>

          {/* Bottle Cases Per Month */}
          <div data-id="element-104">
            <label className="flex items-center justify-between mb-3" data-id="element-105">
              <span className="text-base font-medium text-gray-900" data-id="element-106">
                Cases (12 × 1L) Used Monthly
              </span>
              <span className="text-2xl font-light text-gray-900" data-id="element-107">
                {bottleCasesPerMonth}
              </span>
            </label>
            <input type="range" min="5" max="100" step="5" value={bottleCasesPerMonth} onChange={e => setBottleCasesPerMonth(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900" data-id="element-108" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-109">
              <span data-id="element-110">5</span>
              <span data-id="element-111">100</span>
            </div>
          </div>

          {/* Case Cost */}
          <div data-id="element-112">
            <label className="flex items-center justify-between mb-3" data-id="element-113">
              <span className="text-base font-medium text-gray-900" data-id="element-114">
                Cost Per Case ({currencySymbol}
                {caseCostMin}–{currencySymbol}
                {caseCostMax})
              </span>
              <span className="text-2xl font-light text-gray-900" data-id="element-115">
                {currencySymbol}
                {caseCost}
              </span>
            </label>
            <input type="range" min={caseCostMin} max={caseCostMax} step="1" value={caseCost} onChange={e => {
            const val = Number(e.target.value);
            setCaseCostUSD(isCAD ? Math.round(val / CAD_RATE) : val);
          }} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900" data-id="element-116" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-117">
              <span data-id="element-118">
                {currencySymbol}
                {caseCostMin}
              </span>
              <span data-id="element-119">
                {currencySymbol}
                {caseCostMax}
              </span>
            </div>
          </div>

          {/* Menu Price */}
          <div data-id="element-120">
            <label className="flex items-center justify-between mb-3" data-id="element-121">
              <span className="text-base font-medium text-gray-900" data-id="element-122">
                WELLHAUS Menu Price Per Person
              </span>
              <span className="text-2xl font-light text-gray-900" data-id="element-123">
                {currencySymbol}
                {menuPrice.toFixed(2)}
              </span>
            </label>
            <input type="range" min={menuPriceMin} max={menuPriceMax} step={menuPriceStep} value={menuPrice} onChange={e => {
            const val = Number(e.target.value);
            setMenuPriceUSD(isCAD ? +(val / CAD_RATE).toFixed(2) : val);
          }} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900" data-id="element-124" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-125">
              <span data-id="element-126">
                {currencySymbol}
                {menuPriceMin.toFixed(2)}
              </span>
              <span data-id="element-127">
                {currencySymbol}
                {menuPriceMax.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6" data-id="element-128">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 text-white" data-id="element-129">
            <h4 className="text-sm font-medium text-gray-300 mb-4" data-id="element-130">
              WELLHAUS™ Drinkstation
            </h4>
            <div className="space-y-3" data-id="element-131">
              <div className="flex justify-between items-center" data-id="element-132">
                <span className="text-gray-200" data-id="element-133">Monthly Lease</span>
                <span className="text-xl font-light" data-id="element-134">
                  -{currencySymbol}
                  {formatNumber(leaseCost)}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-135">
                <span className="text-gray-200" data-id="element-136">Monthly Revenue</span>
                <span className="text-xl font-light text-green-400" data-id="element-137">
                  +{currencySymbol}
                  {formatNumber(wellhausMonthlyRevenue)}
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-138">
                <span className="text-gray-200" data-id="element-139">Savings on Bottled Water</span>
                <span className="text-xl font-light text-green-400" data-id="element-140">
                  +{currencySymbol}
                  {formatNumber(traditionalMonthlyCost)}
                </span>
              </div>
              <div className="border-t border-gray-600 pt-3 mt-3" data-id="element-141">
                <div className="flex justify-between items-center" data-id="element-142">
                  <span className="font-medium" data-id="element-143">Net Monthly Profit</span>
                  <span className="text-2xl font-light text-green-400" data-id="element-144">
                    +{currencySymbol}
                    {formatNumber(wellhausNetProfit)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6" data-id="element-145">
            <h4 className="text-sm font-medium text-green-900 mb-4" data-id="element-146">
              Your Annual Profit
            </h4>
            <div className="flex justify-between items-center" data-id="element-147">
              <span className="text-green-900 font-medium" data-id="element-148">Annual Profit</span>
              <span className="text-3xl font-light text-green-600" data-id="element-149">
                +{currencySymbol}
                {formatNumber(annualProfit)}
              </span>
            </div>
          </div>

          <div className="text-sm text-gray-600 italic" data-id="element-150">
            * Based on {formatNumber(waterCustomersPerMonth)} water orders per
            month ({dailyCustomers} customers/day × 30 days ×{' '}
            {orderingPercentage}%)
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center" data-id="element-151">
        <p className="text-base text-gray-700 mb-4" data-id="element-152">
          <strong data-id="element-153">Plus additional benefits:</strong> Reduced plastic waste,
          premium brand positioning, improved customer satisfaction
          {country === 'USA' && ', and Section 179 tax deductions'}.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-154">
          <a href="https://form.typeform.com/to/ghKfSrez" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-gray-900 text-white text-base font-medium rounded-full hover:bg-gray-800 transition-colors" data-id="element-155">
            Request a Demo
          </a>
          <a href="https://calendly.com/-smartsoda/discoverycall" target="_blank" rel="noopener noreferrer" className="px-8 py-3 border-2 border-gray-900 text-gray-900 text-base font-medium rounded-full hover:bg-gray-900 hover:text-white transition-colors" data-id="element-156">
            Contact Sales
          </a>
        </div>
      </div>
    </div>;
}